import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Flame, Globe2, Goal, Clapperboard, Share2, Target, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { trackEvent } from "@/lib/audit";
import {
  getAllForDay,
  getCounter,
  getDailySeries,
  getFirstPredictionBuckets,
  ymd,
} from "@/lib/warRoom/eventCounters";
import { loadFirst72 } from "@/lib/warRoom/firstHoursTelemetry";
import { getWarRoomProduction, type WarRoomProduction } from "@/lib/warRoom/warRoomProduction.functions";

export const Route = createFileRoute("/admin/world-cup-warroom")({
  ssr: false,
  head: () => ({ meta: [{ title: "🚀 Activación Mundial 72h · VerseSport" }] }),
  component: WarRoomPage,
});

function WarRoomPage() {
  const { authReady } = useAuth();
  const { isSuperAdmin, isLoading } = useSuperAdmin();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isSuperAdmin) return;
    void trackEvent("warroom_dashboard_opened", { at: Date.now() });
    const id = window.setInterval(() => setTick((n) => n + 1), 5_000);
    return () => window.clearInterval(id);
  }, [isSuperAdmin]);

  if (!authReady || isLoading) {
    return <main className="p-6 text-sm text-muted-foreground">Cargando…</main>;
  }
  if (!isSuperAdmin) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-xl font-bold">Acceso restringido</h1>
          <p className="mt-2 text-sm text-muted-foreground">Requiere rol superadmin.</p>
          <Link to="/" className="mt-4 inline-block text-primary underline">Volver</Link>
        </div>
      </main>
    );
  }

  return <WarRoomDashboard key={tick} />;
}

function WarRoomDashboard() {
  const today = ymd();
  const all = getAllForDay(today);
  const first72 = loadFirst72();

  // ---- Source toggle (LOCAL / PRODUCTION) ----
  const [source, setSource] = useState<"production" | "local">("production");
  const fetchProd = useServerFn(getWarRoomProduction);
  const prodQuery = useQuery<WarRoomProduction>({
    queryKey: ["warroom", "production"],
    queryFn: () => fetchProd(),
    refetchInterval: 30_000,
    retry: 1,
    staleTime: 20_000,
  });
  const prod = prodQuery.data;
  const prodFailed = prodQuery.isError || (!!prod && Object.keys(prod.errors).length > 0 && !prod.funnels);
  const effectiveSource: "production" | "local" = source === "production" && !prodQuery.isError ? "production" : "local";

  useEffect(() => {
    if (prodQuery.isError) void trackEvent("warroom_query_failed", { reason: String(prodQuery.error) });
    if (prod) void trackEvent("warroom_production_loaded", { errors: Object.keys(prod.errors).length });
  }, [prod, prodQuery.isError, prodQuery.error]);
  useEffect(() => { void trackEvent("warroom_source_changed", { source }); }, [source]);

  // ---- Funnel (W-01) ----
  const visitors = Math.max(1, all["activation_started"] ?? 0);
  const nationCount = all["nation_selected"] ?? 0;
  const predCount = all["first_prediction_completed"] ?? all["prediction_vote"] ?? 0;
  const shareCount = all["first_share_completed"] ?? all["viral_share_completed"] ?? 0;
  const refCount = all["first_referral_sent"] ?? all["share_link_generated"] ?? 0;
  const returnCount = all["returned_same_day"] ?? 0;

  const pct = (n: number, base = visitors) => (base > 0 ? Math.round((n / base) * 100) : 0);

  // ---- W-02 First-prediction timer ----
  const buckets = getFirstPredictionBuckets();
  const totalBuckets = Object.values(buckets).reduce((a, b) => a + b, 0);
  const under60 = (buckets["0-10s"] ?? 0) + (buckets["10-30s"] ?? 0) + (buckets["30-60s"] ?? 0);
  const under60Pct = totalBuckets > 0 ? Math.round((under60 / totalBuckets) * 100) : 0;

  // ---- W-03 Daily WOW ----
  const wowShown = all["daily_wow_card_action"] ?? 0;
  const wowShared = all["daily_wow_shared"] ?? 0;

  // ---- W-04 Viralidad ----
  const shareOpen = all["global_share_clicked"] ?? 0;
  const sharesTotal = all["viral_share_completed"] ?? 0;
  const shareSuccess = all["share_success_feedback_shown"] ?? 0;
  const kFactor = visitors > 0 ? (refCount / visitors).toFixed(2) : "0.00";

  // ---- W-05 Challenge ----
  const chSent = all["challenge_friend_sent"] ?? all["challenge_friend_clicked"] ?? 0;
  const chAccepted = all["challenge_friend_accepted"] ?? 0;
  const chRate = chSent > 0 ? Math.round((chAccepted / chSent) * 100) : 0;

  // ---- W-07 Clip of the day ----
  const clipShown = all["clip_of_day_shown"] ?? 0;
  const clipOpen = all["clip_of_day_opened"] ?? 0;
  const clipCtr = clipShown > 0 ? Math.round((clipOpen / clipShown) * 100) : 0;

  // ---- W-08 Retention D1 ----
  const series = getDailySeries("activation_started", 3);
  const yesterday = series[series.length - 2]?.n ?? 0;
  const returnsToday = returnCount;
  const retentionPct = yesterday > 0 ? Math.round((returnsToday / yesterday) * 100) : 0;
  const retState: "good" | "warn" | "bad" = retentionPct >= 25 ? "good" : retentionPct >= 15 ? "warn" : "bad";

  // ---- Alerts ----
  const alerts = useMemo(() => {
    if (effectiveSource === "production" && prod) {
      return prod.alerts.map((a) => `${a.severity === "red" ? "🔴" : "🟡"} ${a.label}`);
    }
    const a: string[] = [];
    if (pct(nationCount) < 40) a.push(`🔴 Activación ${pct(nationCount)}% (<40%)`);
    if (pct(predCount) < 25) a.push(`🔴 Predicción ${pct(predCount)}% (<25%)`);
    if (pct(shareCount) < 5) a.push(`🔴 Shares ${pct(shareCount)}% (<5%)`);
    if (retentionPct < 15) a.push(`🔴 Retención D1 ${retentionPct}% (<15%)`);
    if (pct(refCount) < 2) a.push(`🔴 Referral ${pct(refCount)}% (<2%)`);
    return a;
  }, [effectiveSource, prod, nationCount, predCount, shareCount, refCount, retentionPct, visitors]);

  useEffect(() => {
    if (effectiveSource === "production" && alerts.length > 0) {
      void trackEvent("warroom_alert_triggered", { count: alerts.length, source: effectiveSource });
    }
  }, [alerts, effectiveSource]);

  // ---- Production overrides (used when effectiveSource === "production") ----
  // Default everything to locals; selectively override with prod values.
  const useProd = effectiveSource === "production" && !!prod;
  const f24 = useProd ? prod!.funnels?.find((x) => x.window === "24h") : undefined;
  const f72 = useProd ? prod!.funnels?.find((x) => x.window === "72h") : undefined;
  const f7d = useProd ? prod!.funnels?.find((x) => x.window === "7d") : undefined;

  const dVisitors = f24?.visitors ?? visitors;
  const dGet = (k: string) => f24?.steps.find((s) => s.key === k)?.n ?? 0;
  const dNation = useProd ? dGet("nation_selected") : nationCount;
  const dPred = useProd ? dGet("first_prediction_completed") : predCount;
  const dShare = useProd ? dGet("first_share_completed") : shareCount;
  const dRef = useProd ? dGet("first_referral_sent") : refCount;
  const dReturn = useProd ? dGet("returned_same_day") : returnCount;
  const dPct = (n: number, base = dVisitors) => (base > 0 ? Math.round((n / base) * 100) : 0);

  const dBuckets = useProd && prod!.firstPrediction ? prod!.firstPrediction.buckets : buckets;
  const dBucketsTotal = Object.values(dBuckets).reduce((a, b) => a + b, 0);
  const dUnder60Pct = useProd && prod!.firstPrediction ? prod!.firstPrediction.under60Pct : under60Pct;

  const dWowShown = useProd && prod!.dailyWow ? prod!.dailyWow.shown : wowShown;
  const dWowShared = useProd && prod!.dailyWow ? prod!.dailyWow.shared : wowShared;

  const dShares = useProd && prod!.virality ? prod!.virality.shares : sharesTotal;
  const dReferrals = useProd && prod!.virality ? prod!.virality.referrals : refCount;
  const dKFactor = useProd && prod!.virality ? prod!.virality.coefficient.toFixed(2) : kFactor;

  const dChSent = useProd && prod!.challenge ? prod!.challenge.sent : chSent;
  const dChAccepted = useProd && prod!.challenge ? prod!.challenge.accepted : chAccepted;
  const dChRate = useProd && prod!.challenge ? prod!.challenge.rate : chRate;

  const dClipShown = useProd && prod!.media ? prod!.media.topByViews.reduce((a, b) => a + b.views, 0) : clipShown;
  const dClipOpen = useProd && prod!.media ? prod!.media.topByViews.reduce((a, b) => a + b.clicks, 0) : clipOpen;
  const dClipCtr = useProd && prod!.media ? prod!.media.ctrAvg : clipCtr;

  const dRetD1 = useProd && prod!.retention ? prod!.retention.d1Pct : retentionPct;
  const dRetState = useProd && prod!.retention ? prod!.retention.state : retState;
  const dD0 = useProd && prod!.retention ? prod!.retention.d0 : (series[series.length - 1]?.n ?? 0);
  const dD1 = useProd && prod!.retention ? prod!.retention.d1 : yesterday;

  return (
    <main className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      {prodFailed && source === "production" && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          ⚠️ No se pudo cargar producción — mostrando fallback local.
        </div>
      )}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">🚀 Activación Mundial 72h</h1>
          <p className="text-xs text-muted-foreground">
            Sala de guerra · {today} · fuente <span className="font-bold uppercase text-foreground">{effectiveSource}</span>
            {effectiveSource === "production" && prod ? ` · datos generados ${new Date(prod.generatedAt).toLocaleTimeString()}` : " · device-local"}
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-border bg-background/60 p-0.5 text-[10px] uppercase tracking-widest">
          {(["production", "local"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSource(s)}
              className={`px-3 py-1 rounded-full transition ${source === s ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </header>

      {alerts.length > 0 && (
        <section className="rounded-xl border border-destructive/40 bg-destructive/10 p-3">
          <div className="flex items-center gap-2 text-sm font-bold text-destructive">
            <AlertTriangle className="h-4 w-4" /> Alertas activas
          </div>
          <ul className="mt-1 text-xs space-y-0.5">
            {alerts.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </section>
      )}

      {/* W-10 Command Center summary */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Block icon={<Flame className="h-4 w-4" />} title="🔥 Activación">
          <Mini label="Nuevos" value={dVisitors} />
          <Mini label="→ Nación" value={`${dPct(dNation)}%`} />
          <Mini label="→ Pred" value={`${dPct(dPred)}%`} />
        </Block>
        <Block icon={<Globe2 className="h-4 w-4" />} title="🌍 Nation Wars">
          <Mini label="Top país" value={useProd && prod!.nations?.top[0]?.nation_code ? prod!.nations!.top[0].nation_code : (dNation || "—")} />
          <Mini label="Gain" value={useProd && prod!.nations?.biggestGain ? `+${prod!.nations.biggestGain.delta}` : "—"} good={!!(useProd && prod!.nations?.biggestGain)} />
          <Mini label="Drop" value={useProd && prod!.nations?.biggestDrop ? `${prod!.nations.biggestDrop.delta}` : "—"} bad={!!(useProd && prod!.nations?.biggestDrop)} />
        </Block>
        <Block icon={<Goal className="h-4 w-4" />} title="⚽ Predicciones">
          <Mini label="Total" value={dPred} />
          <Mini label="<60s" value={`${dUnder60Pct}%`} good={dUnder60Pct >= 60} />
          <Mini label="Retos" value={dChSent} />
        </Block>
        <Block icon={<Clapperboard className="h-4 w-4" />} title="🎬 Media Blitz">
          <Mini label="Views" value={dClipShown} />
          <Mini label="Clicks" value={dClipOpen} />
          <Mini label="CTR" value={`${dClipCtr}%`} />
        </Block>
        <Block icon={<Share2 className="h-4 w-4" />} title="📤 Viralidad">
          <Mini label="Shares" value={dShares} />
          <Mini label="Referrals" value={dReferrals} />
          <Mini label="K" value={dKFactor} />
        </Block>
        <Block icon={<Target className="h-4 w-4" />} title="🎯 Retención">
          <Mini label="D0" value={dD0} />
          <Mini label="D1" value={dD1} />
          <Mini label="D1 %" value={`${dRetD1}%`} good={dRetState === "good"} warn={dRetState === "warn"} bad={dRetState === "bad"} />
        </Block>
      </section>

      {/* W-01 Funnel */}
      <Panel title={`Embudo de activación (W-01) · ${useProd ? "producción 24h / 72h / 7d" : "local hoy"}`}>
        {useProd && prod ? (
          <div className="grid gap-4 md:grid-cols-3">
            {(["24h", "72h", "7d"] as const).map((w) => {
              const f = prod.funnels?.find((x) => x.window === w);
              if (!f) return <div key={w} className="text-xs text-muted-foreground">{w}: sin datos</div>;
              const labels: Record<string, string> = {
                activation_started: "Visitante",
                nation_selected: "Nación",
                first_prediction_completed: "Predicción",
                first_share_completed: "Share",
                first_referral_sent: "Referral",
                returned_same_day: "Retorno",
              };
              return (
                <div key={w}>
                  <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">{w}</div>
                  <Funnel steps={f.steps.map((s) => ({ label: labels[s.key] ?? s.key, n: s.n }))} />
                </div>
              );
            })}
          </div>
        ) : (
          <Funnel
            steps={[
              { label: "Visitante", n: visitors },
              { label: "Nación", n: nationCount },
              { label: "Predicción", n: predCount },
              { label: "Share", n: shareCount },
              { label: "Referral", n: refCount },
              { label: "Retorno", n: returnCount },
            ]}
          />
        )}
      </Panel>

      <div className="grid gap-4 md:grid-cols-2">
        {/* W-02 First Prediction Timer */}
        <Panel title="⏱ First Prediction Timer (W-02)" subtitle="Meta: 60% bajo 60s">
          <div className="space-y-1.5 text-xs">
            {(["0-10s","10-30s","30-60s","60-120s","120s+"] as const).map((b) => {
              const n = dBuckets[b];
              const p = dBucketsTotal > 0 ? (n / dBucketsTotal) * 100 : 0;
              return (
                <div key={b}>
                  <div className="flex justify-between"><span className="font-bold">{b}</span><span className="tabular-nums">{n}</span></div>
                  <div className="h-1.5 rounded bg-border/40 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${p}%` }} /></div>
                </div>
              );
            })}
            <div className="pt-2 text-[11px] text-muted-foreground">
              <span className={dUnder60Pct >= 60 ? "text-emerald-500 font-bold" : "text-amber-500 font-bold"}>{dUnder60Pct}%</span> bajo 60s
              {useProd && prod!.firstPrediction
                ? <> · n={prod!.firstPrediction.sample} · mediana {Math.round(prod!.firstPrediction.median / 1000)}s · promedio {Math.round(prod!.firstPrediction.avg / 1000)}s</>
                : <> · primer dato local: {first72.firstSeenAt ? new Date(first72.firstSeenAt).toLocaleString() : "—"}</>}
            </div>
          </div>
        </Panel>

        {/* W-03 Daily WOW */}
        <Panel title="🎁 Daily WOW (W-03)">
          <Row label="Shown" value={dWowShown} />
          <Row label="Shared" value={dWowShared} />
          <Row label="Conv. share" value={`${useProd && prod!.dailyWow ? prod!.dailyWow.conversion : (dWowShown > 0 ? Math.round((dWowShared / dWowShown) * 100) : 0)}%`} />
          {useProd && prod!.dailyWow ? (
            <>
              <Row label="Dismissed" value={prod!.dailyWow.dismissed} />
              <Row label="CTA clicks" value={prod!.dailyWow.ctaClicked} />
              <Row label="Top día" value={prod!.dailyWow.topDays[0]?.day ?? "—"} />
              <Row label="Top país" value={prod!.dailyWow.topCountries[0]?.country ?? "—"} />
            </>
          ) : null}
        </Panel>

        {/* W-04 Virality */}
        <Panel title="📤 Virality Dashboard (W-04)">
          <Row label="Shares" value={dShares} />
          <Row label="Referrals" value={dReferrals} />
          <Row label="Conversiones" value={useProd && prod!.virality ? prod!.virality.conversions : 0} />
          <Row label="K-Factor" value={dKFactor} />
          {useProd && prod!.virality ? (
            <>
              <div className="pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">Top canales</div>
              {prod!.virality.channels.slice(0, 4).map((c) => <Row key={c.channel} label={c.channel} value={c.n} />)}
              <div className="pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">Usuarios más virales</div>
              {prod!.virality.topUsers.slice(0, 3).map((u) => <Row key={u.user_id} label={u.user_id.slice(0, 8) + "…"} value={u.n} />)}
            </>
          ) : null}
        </Panel>

        {/* W-05 Challenge Friend */}
        <Panel title="🥊 Challenge Friend (W-05)">
          <Row label="Enviados" value={dChSent} />
          <Row label="Aceptados" value={dChAccepted} />
          <Row label="Won" value={useProd && prod!.challenge ? prod!.challenge.won : 0} />
          <Row label="Lost" value={useProd && prod!.challenge ? prod!.challenge.lost : 0} />
          <Row label="Ignored" value={useProd && prod!.challenge ? prod!.challenge.ignored : 0} />
          <Row label="Conv." value={`${dChRate}%`} />
          {useProd && prod!.challenge ? (
            <>
              <Row label="Top partido" value={prod!.challenge.topMatches[0]?.match ?? "—"} />
              <Row label="Top país" value={prod!.challenge.topCountries[0]?.country ?? "—"} />
            </>
          ) : null}
        </Panel>

        {/* W-06 Nation Wars Health */}
        <Panel title="🌍 Nation Wars Health (W-06)" subtitle={useProd ? "nation_scores + daily" : "Momentum local"}>
          {useProd && prod!.nations ? (
            <>
              {prod!.nations.top.slice(0, 10).map((n, i) => (
                <Row key={n.nation_code} label={`#${i + 1} ${n.nation_name}`} value={`${n.total_points.toLocaleString()} · ${n.total_users}u`} />
              ))}
              <div className="pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">Momentum 24h</div>
              <Row label="Mayor subida" value={prod!.nations.biggestGain ? `${prod!.nations.biggestGain.nation_code} +${prod!.nations.biggestGain.delta}` : "—"} />
              <Row label="Mayor caída" value={prod!.nations.biggestDrop ? `${prod!.nations.biggestDrop.nation_code} ${prod!.nations.biggestDrop.delta}` : "—"} />
            </>
          ) : (
            <>
              <Row label="Selecciones nación" value={nationCount} />
              <Row label="Rank clicks" value={all["nation_rank_badge_clicked"] ?? 0} />
              <Row label="Momentum mostrado" value={all["nation_momentum_shown"] ?? 0} />
            </>
          )}
        </Panel>

        {/* W-07 Clip of the Day */}
        <Panel title="🎬 Media Blitz / Clip of the Day (W-07)" subtitle={useProd ? "viral_clips 7d" : "local"}>
          {useProd && prod!.media ? (
            <>
              <Row label="CTR promedio" value={`${prod!.media.ctrAvg}%`} />
              <div className="pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">Top por views</div>
              {prod!.media.topByViews.slice(0, 5).map((c) => (
                <Row key={c.id} label={c.label} value={`${c.views.toLocaleString()}v · ${c.shares}s`} />
              ))}
              <div className="pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">Top por shares</div>
              {prod!.media.topByShares.slice(0, 5).map((c) => (
                <Row key={c.id} label={c.label} value={`${c.shares.toLocaleString()}s · ${c.views}v`} />
              ))}
            </>
          ) : (
            <>
              <Row label="Impresiones" value={clipShown} />
              <Row label="Clicks" value={clipOpen} />
              <Row label="CTR" value={`${clipCtr}%`} />
            </>
          )}
        </Panel>

        {/* W-08 Retention D1 */}
        <Panel title="🎯 Retention D1 Watch (W-08)" subtitle="Meta: 25%">
          <div className="grid grid-cols-4 gap-2 text-xs">
            <Mini label="D0" value={dD0} />
            <Mini label="D1" value={dD1} />
            <Mini label="D3" value={useProd && prod!.retention ? prod!.retention.d3 : "—"} />
            <Mini label="D7" value={useProd && prod!.retention ? prod!.retention.d7 : "—"} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs mt-2">
            <Mini label="D1 %" value={`${dRetD1}%`} good={dRetState==="good"} warn={dRetState==="warn"} bad={dRetState==="bad"} />
            <Mini label="D3 %" value={`${useProd && prod!.retention ? prod!.retention.d3Pct : 0}%`} />
            <Mini label="D7 %" value={`${useProd && prod!.retention ? prod!.retention.d7Pct : 0}%`} />
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {dRetState === "bad" && <span className="text-destructive">🔴 Crítico</span>}
            {dRetState === "warn" && <span className="text-amber-500">🟡 Atención</span>}
            {dRetState === "good" && <span className="text-emerald-500">🟢 Saludable</span>}
          </p>
        </Panel>

        {/* W-09 Drop-off */}
        <Panel title="💧 Drop-off Detector (W-09)">
          <DropOffList
            funnel={useProd && prod!.dropOff
              ? prod!.dropOff.stages.map((s) => ({ stage: s.stage, lost: s.lost }))
              : [
              { stage: "Home → Nation", lost: Math.max(0, visitors - nationCount) },
              { stage: "Nation → Predicción", lost: Math.max(0, nationCount - predCount) },
              { stage: "Predicción → Share", lost: Math.max(0, predCount - shareCount) },
              { stage: "Share → Referral", lost: Math.max(0, shareCount - refCount) },
              { stage: "Día 0 → Día 1", lost: Math.max(0, yesterday - returnCount) },
            ]}
          />
        </Panel>

        {/* W-11 Audio Sync Usage */}
        <Panel title="🎧 Audio Sync Usage (W-11)" subtitle="Meta: 15% usuarios activos">
          <Row label="Card shown" value={all["audio_sync_card_shown"] ?? 0} />
          <Row label="Card CTA" value={all["audio_sync_card_cta_clicked"] ?? 0} />
          <Row label="Sync iniciado" value={all["audio_sync_started"] ?? 0} />
          <Row label="Sync completado" value={all["audio_sync_completed"] ?? 0} />
          <Row label="Tour shown" value={all["audio_sync_tour_shown"] ?? 0} />
        </Panel>

        {/* W-12 Challenge invites & retention triggers */}
        <Panel title="⚔️ Challenge Invites + Triggers (W-12)">
          <Row label="Invitaciones recibidas" value={all["challenge_invite_received"] ?? 0} />
          <Row label="Invitaciones aceptadas" value={all["challenge_invite_accepted"] ?? 0} />
          <Row label="Invitaciones descartadas" value={all["challenge_invite_dismissed"] ?? 0} />
          <Row label="Resultados compartidos" value={all["challenge_result_shared"] ?? 0} />
          <Row label="Triggers notif. preparados" value={all["notification_trigger_prepared"] ?? 0} />
          <Row label="Day-2 panel shown" value={all["day2_return_panel_shown"] ?? 0} />
        </Panel>
      </div>

      <footer className="pt-4 text-[10px] text-muted-foreground">
        Estado local · device-scoped · sin nuevas tablas · derivado del bus <code>vs:event</code>.
      </footer>
    </main>
  );
}

/* ---------- shared UI ---------- */

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border/60 bg-background/50 p-4 space-y-2">
      <header>
        <h2 className="text-sm font-black">{title}</h2>
        {subtitle && <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{subtitle}</p>}
      </header>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Block({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest">{icon}{title}</div>
      <div className="mt-2 space-y-1 text-xs">{children}</div>
    </div>
  );
}

function Mini({ label, value, good, warn, bad }: { label: string; value: string | number; good?: boolean; warn?: boolean; bad?: boolean }) {
  const cls = bad ? "text-destructive" : warn ? "text-amber-500" : good ? "text-emerald-500" : "text-foreground";
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={`font-black tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between border-b border-border/30 py-1 last:border-b-0">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className="text-xs font-black tabular-nums">{value}</span>
    </div>
  );
}

function Funnel({ steps }: { steps: { label: string; n: number }[] }) {
  const base = Math.max(1, steps[0]?.n ?? 1);
  return (
    <div className="space-y-1.5">
      {steps.map((s, i) => {
        const p = (s.n / base) * 100;
        const prev = i > 0 ? steps[i - 1].n : s.n;
        const conv = prev > 0 ? Math.round((s.n / prev) * 100) : 100;
        return (
          <div key={s.label}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold">{s.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {s.n} · {Math.round(p)}% del top {i > 0 && <span className="ml-2 text-foreground">({conv}% conv)</span>}
              </span>
            </div>
            <div className="h-2 rounded bg-border/40 overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${Math.max(2, p)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DropOffList({ funnel }: { funnel: { stage: string; lost: number }[] }) {
  const sorted = [...funnel].sort((a, b) => b.lost - a.lost);
  const max = Math.max(1, ...sorted.map((s) => s.lost));
  return (
    <div className="space-y-1.5 text-xs">
      {sorted.map((s, i) => (
        <div key={s.stage} className="flex items-center gap-2">
          <span className="w-5 text-right font-black tabular-nums text-muted-foreground">{i + 1}</span>
          <span className="flex-1">{s.stage}</span>
          <span className="tabular-nums font-bold flex items-center gap-1">
            {i === 0 ? <TrendingDown className="h-3 w-3 text-destructive" /> : <TrendingUp className="h-3 w-3 text-muted-foreground" />}
            {s.lost}
          </span>
          <div className="w-24 h-1.5 rounded bg-border/40 overflow-hidden">
            <div className="h-full bg-destructive" style={{ width: `${(s.lost / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
